/**
 * httpCall.js
 *
 * Provides a generic API utility for making HTTP requests using axios.
 * Supports GET, POST, PUT, PATCH, DELETE methods.
 * Handles errors and displays response dialogs via Vuex.
 */

import axios from 'axios';

// HTTP method constants
import { GET, POST, PUT, PATCH, DELETE } from '@/shared/constants/httpMethods';
// Vuex mutation type for response dialog
import { SET_RESPONSE_DIALOG } from '@/shared/mutations/type';

/**
 * Makes an HTTP request using the specified method, URL, and body.
 * Handles errors and commits response dialogs to the Vuex store.
 *
 * @param {string} method - HTTP method (GET, POST, PUT, PATCH, DELETE)
 * @param {string} url - The endpoint URL
 * @param {object} [body] - The request payload or config
 * @returns {Promise<{statusCode: number|null, data: any}>} The response data and status code
 */
export const api = async (method, url, body) => {
  const responseData = {
    statusCode: null,
    data: null,
  };

  let response = null;

  try {
    // Select the axios method based on the HTTP method constant
    switch (method) {
      case GET:
        response = await axios.get(url, body);
        break;
      case POST:
        response = await axios.post(url, body);
        break;
      case PUT:
        response = await axios.put(url, body);
        break;
      case PATCH:
        response = await axios.patch(url, body);
        break;
      case DELETE:
        response = await axios.delete(url, body);
        break;
      default:
        throw new Error(`Unsupported HTTP method: ${method}`);
    }

    responseData.statusCode = response.status;
    responseData.data = response.data;
  } catch (error) {
    // Prepare error dialog object for Vuex
    const errorObject = {
      visible: true,
      isSuccess: false,
      message: null,
      subMessage: null,
      errors: [],
    };

    if (error.code === 'ERR_NETWORK') {
      responseData.statusCode = 503;
      errorObject.message = 'Connection refused';
      errorObject.subMessage = 'Sorry, we are currently experiencing connection issues. Please try again later.';
    } else {
      responseData.statusCode = 400;
      errorObject.message = 'Error';
      errorObject.subMessage = 'Something went wrong with the request. Please try again later. If the issue persists, contact support for assistance.';
    }

    // Commit error dialog to Vuex store
    window.App.$store.commit(SET_RESPONSE_DIALOG, errorObject);
  }

  return responseData;
};
